import type { ValueFormatterParams, CellClassParams, ColDef, ColGroupDef } from 'ag-grid-community'

// ─── Number Formatting ───

/**
 * Format a number with compact notation (K, M, B, T).
 * E.g., 1234567 → "1.23M"
 */
export function formatCompactNumber(value: number): string {
  const abs = Math.abs(value)
  const trim = (n: string) => n.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1')
  if (abs >= 1e12) return `${trim((value / 1e12).toFixed(2))}T`
  if (abs >= 1e9) return `${trim((value / 1e9).toFixed(2))}B`
  if (abs >= 1e6) return `${trim((value / 1e6).toFixed(2))}M`
  if (abs >= 1e3) return `${trim((value / 1e3).toFixed(2))}K`
  return trim(value.toFixed(2))
}

/**
 * Parse a compact number string back to a number.
 * E.g., "100k" → 100000, "1.5M" → 1500000, "2b" → 2000000000, ".5" → 0.5
 * Accepts leading-decimal (".5") and trailing-dot ("5.") forms.
 * Returns null for empty/unparseable strings.
 */
export function parseCompactNumber(input: string): number | null {
  const trimmed = input.trim()
  if (!trimmed) return null

  const match = trimmed.match(/^(-?(?:\d+\.?\d*|\.\d+))\s*([kmbtKMBT])?$/)
  if (!match) return null

  const num = parseFloat(match[1])
  if (isNaN(num)) return null

  const suffix = match[2]?.toLowerCase()
  if (!suffix) return num

  const multipliers: Record<string, number> = { k: 1e3, m: 1e6, b: 1e9, t: 1e12 }
  return num * multipliers[suffix]
}

/**
 * Format a number with commas and optional decimal places.
 */
export function formatNumber(value: number, decimals = 2): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

// ─── Date / Time Formatting ───

type DateInput = string | number | Date | null | undefined

function toDateOrNull(value: DateInput): Date | null {
  if (value == null) return null
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value
  }
  if (typeof value === 'number') {
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d
  }
  // string — try to parse, falling back to ISO date if it looks like one
  const d = new Date(value)
  return isNaN(d.getTime()) ? null : d
}

/**
 * Format a date as YYYY-MM-DD (ISO date).
 * Accepts ISO strings (returns the date portion via slice), epoch ms, or Date.
 * Returns '' for null / undefined / unparseable input.
 */
export function formatIsoDate(value: DateInput): string {
  if (value == null) return ''
  // ISO-style strings: trust the leading 10 chars (avoids tz drift around midnight).
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
    const d = toDateOrNull(value)
    return d ? d.toISOString().slice(0, 10) : ''
  }
  const d = toDateOrNull(value)
  return d ? d.toISOString().slice(0, 10) : ''
}

/**
 * Format a date as a short label, locale-pinned to en-US.
 * Default: "Oct 28". With { withYear: true }: "Oct 28, 2024".
 */
export function formatShortDate(value: DateInput, options: { withYear?: boolean } = {}): string {
  const d = toDateOrNull(value)
  if (!d) return ''
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(options.withYear ? { year: 'numeric' } : {}),
  })
}

/**
 * Format a time, locale-pinned to en-US.
 * Default: "10:23 AM" (12h, no seconds). With { seconds: true }: "10:23:45 AM".
 * With { hour12: false }: "10:23" or "10:23:45" (24h).
 */
export function formatTime(
  value: DateInput,
  options: { hour12?: boolean; seconds?: boolean } = {},
): string {
  const d = toDateOrNull(value)
  if (!d) return ''
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    ...(options.seconds ? { second: '2-digit' } : {}),
    hour12: options.hour12 ?? true,
  })
}

// ─── Value Formatters (AG Grid) ───

/**
 * Currency formatter: $1,234.56
 */
export function currencyFormatter(params: ValueFormatterParams): string {
  if (params.value == null) return ''
  const val = Number(params.value)
  if (isNaN(val)) return ''
  return `$${formatNumber(val)}`
}

/**
 * Percentage formatter: 12.34%
 */
export function percentFormatter(params: ValueFormatterParams): string {
  if (params.value == null) return ''
  const val = Number(params.value)
  if (isNaN(val)) return ''
  return `${formatNumber(val)}%`
}

/**
 * Compact number formatter: 1.23M
 */
export function compactFormatter(params: ValueFormatterParams): string {
  if (params.value == null) return ''
  const val = Number(params.value)
  if (isNaN(val)) return ''
  return formatCompactNumber(val)
}

/**
 * Integer formatter: 1,234 (no decimals)
 */
export function integerFormatter(params: ValueFormatterParams): string {
  if (params.value == null) return ''
  const val = Number(params.value)
  if (isNaN(val)) return ''
  return formatNumber(val, 0)
}

/**
 * AG Grid: ISO date (YYYY-MM-DD).
 */
export function isoDateFormatter(params: ValueFormatterParams): string {
  return formatIsoDate(params.value as DateInput)
}

/**
 * AG Grid: short en-US date (e.g. "Oct 28").
 */
export function shortDateFormatter(params: ValueFormatterParams): string {
  return formatShortDate(params.value as DateInput)
}

/**
 * AG Grid: en-US time with seconds, 12h.
 */
export function timeFormatter(params: ValueFormatterParams): string {
  return formatTime(params.value as DateInput, { seconds: true })
}

/**
 * Generic number formatter with configurable options.
 */
export function createNumberFormatter(opts: {
  prefix?: string
  suffix?: string
  decimals?: number
  compact?: boolean
  zeroValue?: string
}) {
  return (params: ValueFormatterParams): string => {
    if (params.value == null) return ''
    const val = Number(params.value)
    if (isNaN(val)) return ''
    if (val === 0 && opts.zeroValue) return opts.zeroValue

    const formatted = opts.compact
      ? formatCompactNumber(val)
      : formatNumber(val, opts.decimals ?? 2)

    return `${opts.prefix ?? ''}${formatted}${opts.suffix ?? ''}`
  }
}

/**
 * Wraps a formatter to safely handle null/undefined values.
 */
export function guardFormatter(
  formatter: ((params: ValueFormatterParams) => string) | undefined,
  nullFormat = '',
): (params: ValueFormatterParams) => string {
  return (params: ValueFormatterParams) => {
    if (params.value == null) return nullFormat
    if (formatter) return formatter(params)
    return String(params.value)
  }
}

// ─── Cell Class Rules ───

/**
 * Returns a cell class for positive/negative number highlighting.
 */
export function highlightCellClass(params: CellClassParams): string | undefined {
  const val = Number(params.value)
  if (isNaN(val)) return undefined
  if (val > 0) return 'edge-cell-positive'
  if (val < 0) return 'edge-cell-negative'
  return undefined
}

/**
 * Row class rules for common patterns.
 */
export const rowClassRules: Record<
  string,
  (params: { data?: Record<string, unknown> }) => boolean
> = {
  'halted-row': (params) => !!params?.data?.halt,
  'keyword-bar-row': (params) => params?.data?.__isKeywordBar === true,
}

// ─── Column Definition Helpers ───

/**
 * Legible floor for the auto-fit modes. Fit-to-panel never shrinks a column
 * below this, so numbers stay readable instead of truncating to "1,234…".
 * When a panel is too small to show every column at this width, a horizontal
 * scrollbar appears rather than clipping the values.
 */
export const FIT_MIN_COL_WIDTH = 64

/**
 * Floor for `manual` mode, where the user is sizing columns by hand.
 *
 * AG Grid clamps a UI drag at the column's `minWidth`, and `applyColumnState`
 * clamps the same way on remount, so the fit-mode floor above is what stops a
 * one-digit column (news counts, Y/N flags) from being dragged genuinely
 * narrow. Once someone is sizing by hand, their judgement beats ours; this is
 * only a safety stop, set where the header text and sort arrow still have
 * somewhere to go.
 */
export const MANUAL_MIN_COL_WIDTH = 24

/** Standard column defaults for most grids. */
export const DEFAULT_COL_DEF = {
  sortable: true,
  resizable: true,
  suppressMovable: true,
  suppressHeaderMenuButton: true,
  minWidth: FIT_MIN_COL_WIDTH,
  cellClassRules: {
    'edge-cell-numeric': (params: CellClassParams) => typeof params.value === 'number',
  },
} as const

/**
 * Lower every explicit per-column `minWidth` to `floor` (never raise one).
 *
 * A per-colDef `minWidth` beats `defaultColDef`, so relaxing the grid-wide
 * floor for manual mode is not enough on its own: columns that reserve their
 * own floor (watchlist chips, calendar event, ticker-stats labels) would stay
 * un-draggable while every other column relaxes. `DataGrid` runs this over the
 * incoming colDefs whenever a `minColumnWidth` is in force so every grid gets
 * the same policy without each column builder having to know about it.
 *
 * Columns without an explicit `minWidth` are left alone (they inherit the
 * grid floor from `defaultColDef`); column groups are walked recursively. The
 * input is returned as-is when nothing needs lowering so colDef identity is
 * stable and AG Grid does not reprocess columns for a no-op.
 */
export function lowerColumnFloors<T extends ColDef | ColGroupDef>(defs: T[], floor: number): T[] {
  let changed = false
  const next = defs.map((def) => {
    if ('children' in def && Array.isArray(def.children)) {
      const children = lowerColumnFloors(def.children as (ColDef | ColGroupDef)[], floor)
      if (children === def.children) return def
      changed = true
      return { ...def, children }
    }
    const minWidth = (def as ColDef).minWidth
    if (typeof minWidth !== 'number' || minWidth <= floor) return def
    changed = true
    return { ...def, minWidth: floor }
  })
  return changed ? next : defs
}

/** Common column definition factory. */
export function createColDef(overrides: Record<string, unknown>) {
  return {
    ...DEFAULT_COL_DEF,
    width: 100,
    headerClass: 'edge-header-cell',
    ...overrides,
  }
}

/**
 * Make a set of column definitions safe to re-supply to a mounted grid.
 *
 * AG Grid re-applies two things from a definition every time the definitions
 * are re-supplied (`_updateColumnState` in ag-grid-community): a `sort`, and,
 * for a column with no flex, a `width`. Both undid what the grid was showing.
 * A fit-to-data grid snapped its autosized columns back to the catalog widths
 * on every rebuild, and a default sort on a definition snapped the grid back
 * to it and refetched, so one header click on the scanner cost two fetches
 * and two width jumps (Chris, 2026-09-19). Definitions rebuild on ordinary
 * panel-state changes, so this has to hold for every grid, not one panel.
 *
 * - `width` becomes `initialWidth`, which only sizes a column when it is
 *   created. A hand-dragged width lives in column state and is re-applied by
 *   the resize hook, so nothing is lost.
 * - `sort` and `sortIndex` are dropped once the grid is mounted
 *   (`keepSort: false`): the default has sorted the first paint, and the
 *   user's sort is the grid's to keep from then on. AG Grid leaves the sort
 *   alone when the definition carries none.
 *
 * Identity is preserved when nothing changes, the same contract as
 * lowerColumnFloors, so an unchanged definition does not look changed.
 */
/** Wrapper class that clamps wrapped header titles to two lines (ag-theme-edge.css). */
export const WRAP_HEADERS_CLASS = 'ag-grid-wrap-headers'

/**
 * Panel-wide header wrapping: long column titles wrap instead of clipping and
 * the header row grows to fit (wrapHeaderText + autoHeaderHeight). The data
 * cells are untouched. DataGrid adds WRAP_HEADERS_CLASS to its wrapper, which
 * caps the titles at two lines. Identity is preserved when wrapping is off.
 */
export function withWrappedHeaders<T extends ColDef | ColGroupDef>(defs: T[], wrap: boolean): T[] {
  if (!wrap) return defs
  return defs.map((def) => {
    if ('children' in def && Array.isArray(def.children)) {
      return {
        ...def,
        children: withWrappedHeaders(def.children as (ColDef | ColGroupDef)[], wrap),
      }
    }
    return { ...def, wrapHeaderText: true, autoHeaderHeight: true } as T
  })
}

export function stabilizeColumnDefs<T extends ColDef | ColGroupDef>(
  defs: T[],
  { keepSort }: { keepSort: boolean },
): T[] {
  let changed = false
  const next = defs.map((def) => {
    if ('children' in def && Array.isArray(def.children)) {
      const children = stabilizeColumnDefs(def.children as (ColDef | ColGroupDef)[], { keepSort })
      if (children === def.children) return def
      changed = true
      return { ...def, children }
    }
    const col = def as ColDef
    const moveWidth = col.width != null && col.initialWidth == null
    const dropSort = !keepSort && (col.sort !== undefined || col.sortIndex !== undefined)
    if (!moveWidth && !dropSort) return def
    changed = true
    const { width, sort, sortIndex, ...rest } = col
    return {
      ...rest,
      ...(moveWidth ? { initialWidth: width } : { width }),
      ...(dropSort ? {} : { sort, sortIndex }),
    } as T
  })
  return changed ? next : defs
}
